import { computed, type Ref } from 'vue';
import { useStorage } from '@vueuse/core';
import type { ConnectionConfig, HopConfig } from './use-ssh';
import type { PanelRegion, SplitDirection } from './use-panels';

// persisted state for a single dock region
export type DockState = {
	size: number;
	minimized: boolean;
};

// dock layout — keyed by region
export type LayoutState = {
	left: DockState;
	right: DockState;
	bottom: DockState;
};

// --- persisted split tree ---

// serializable form of a leaf node (panels are stored separately)
export type PersistedLeafNode = {
	type: 'leaf';
	id: string;
};

// serializable form of a branch node
export type PersistedBranchNode = {
	type: 'branch';
	id: string;
	direction: SplitDirection;
	children: PersistedSplitNode[];
	sizes: number[];
};

export type PersistedSplitNode = PersistedLeafNode | PersistedBranchNode;

// a persisted panel entry — just enough to re-open it
export type PersistedPanel = {
	uri: string;
	region: PanelRegion;
	nodeId: string;
};

// persisted open panels, active URIs per region, and split tree structure
export type OpenPanelsState = {
	panels: PersistedPanel[];
	activeUris: Partial<Record<PanelRegion, string>>;
	// split tree per region (null = single leaf, no splits)
	trees?: Partial<Record<PanelRegion, PersistedSplitNode>>;
};

// persisted state for a single connection
export type WorkspaceState = {
	panelState: Record<string, unknown>;
	layout: LayoutState;
	openPanels: OpenPanelsState;
};

const defaultState: WorkspaceState = {
	panelState: {},
	layout: {
		left: { size: 280, minimized: false },
		right: { size: 280, minimized: false },
		bottom: { size: 250, minimized: false },
	},
	openPanels: { panels: [], activeUris: {} },
};

const connectionKey = (config: ConnectionConfig): string => {
	const hopParts = (config.hops ?? []).map(
		(hop: HopConfig) => `${hop.user}@${hop.hostname}:${hop.port}`,
	);
	const destination = `${config.user}@${config.hostname}:${config.port}`;
	const chain = [...hopParts, destination].join('->');
	return `workspace:${chain}`;
};

export const useWorkspace = (configRef: Ref<ConnectionConfig | null>) => {
	const key = computed(() => (configRef.value ? connectionKey(configRef.value) : null));

	// reactive storage — switches backing key when connection changes
	const state = useStorage<WorkspaceState>(
		// useStorage needs a string key; use a placeholder when disconnected
		computed(() => key.value ?? '__disconnected__'),
		structuredClone(defaultState),
		localStorage,
		{ mergeDefaults: true },
	);

	// --- panel-scoped state ---

	const getPanelState = <T>(uri: string): T | null => {
		const val = state.value.panelState[uri];
		return (val as T) ?? null;
	};

	const setPanelState = <T>(uri: string, panelData: T): void => {
		state.value.panelState[uri] = panelData;
	};

	// --- layout ---

	const layout = computed({
		get: () => state.value.layout,
		set: (val: LayoutState) => {
			state.value.layout = val;
		},
	});

	// --- open panels ---

	const openPanels = computed({
		get: () => state.value.openPanels,
		set: (val: OpenPanelsState) => {
			state.value.openPanels = val;
		},
	});

	return {
		state,
		getPanelState,
		setPanelState,
		layout,
		openPanels,
	};
};
